import { _ } from "i18n";
import { useCallback, useState } from "react";
import Modal from "ui-kit/src/Modal";
import { ApprovalModal } from "../ApprovalModal";
import { DeleteModal } from "../DeleteModal";
import { RenameModal } from "../RenameModal";
import { fileNotReadyReason, isFileReady } from "../functions";
import { useBulkVideoDelete } from "../useBulkVideoDelete";
import { BaseTable, type BaseTableSelection } from "./BaseTable";
import { RowActionButton, RowActionLink, rowActionIcons } from "./rowActions";

export function VideosWithoutApprovalTable() {
	const [approvalModal, setApprovalModal] = useState<{ id?: number; show: boolean }>({ show: false });
	const [deleteModal, setDeleteModal] = useState<{ id?: number; show: boolean }>({ show: false });
	const [renameModal, setRenameModal] = useState<{ id?: number; name?: string; show: boolean }>({ show: false });
	const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
	const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(new Set());
	const { deleteVideos, progress } = useBulkVideoDelete();

	const toggle = useCallback((id: number) => {
		setSelectedIds((current) => {
			const next = new Set(current);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const setMany = useCallback((ids: number[], selected: boolean) => {
		setSelectedIds((current) => {
			const next = new Set(current);
			for (const id of ids) {
				if (selected) {
					next.add(id);
				} else {
					next.delete(id);
				}
			}
			return next;
		});
	}, []);

	const clear = useCallback(() => {
		setSelectedIds((current) => (current.size === 0 ? current : new Set()));
	}, []);

	const selection: BaseTableSelection = {
		selectedIds,
		toggle,
		setMany,
		clear,
		isSelectable: (row) => isFileReady(row.apiStatus),
		unselectableReason: (row) => fileNotReadyReason(row.apiStatus),
	};

	const onConfirmBulkDelete = async () => {
		const videoIds = [...selectedIds];
		setShowBulkDeleteModal(false);
		clear();
		await deleteVideos(videoIds);
	};

	const toolbar =
		selectedIds.size > 0 || progress !== undefined ? (
			<div className="bulkActions">
				{progress === undefined ? (
					<>
						<div className="bulkActions-count">{selectedIds.size} selected</div>
						<button className="bulkDeleteButton" type="button" onClick={() => setShowBulkDeleteModal(true)}>
							<span className="icon-new icon-admin-delete icon-white icon-24" />
							<div className="buttonText">delete selected</div>
						</button>
					</>
				) : (
					<div className="bulkActions-count">
						Deleting {progress.deleted + progress.failed} of {progress.total}
					</div>
				)}
			</div>
		) : undefined;

	return (
		<>
			<BaseTable
				preset="waiting-for-approval"
				selection={selection}
				toolbar={toolbar}
				actions={{
					id: "actions",
					key: "actions",
					label: "Actions",
					header: () => "Actions",
					cell: (value, row) => {
						const fileReady = isFileReady(row.apiStatus);

						return (
							<div className="actionsCell">
								<RowActionLink icon={rowActionIcons.view} label="View page" to={`/video-uploads/${row.id}/video`} />
								<RowActionButton
									icon={rowActionIcons.sendToProcessing}
									label="Send to processing"
									onClick={() => setApprovalModal({ show: true, id: row.id })}
								/>
								<RowActionButton
									icon={rowActionIcons.delete}
									label="Delete"
									onClick={() => setDeleteModal({ show: true, id: row.id })}
									disabled={fileReady === false}
									disabledReason={fileNotReadyReason(row.apiStatus)}
								/>
								<RowActionButton
									icon={rowActionIcons.editName}
									label="Edit Name"
									onClick={() => setRenameModal({ show: true, id: row.id, name: row.name })}
								/>
							</div>
						);
					},
				}}
			/>
			<Modal
				show={approvalModal.show}
				onClose={() => setApprovalModal({ show: false })}
				size="sm"
				title={_("admin.videoUploads.metadataEditor.modalApproveTitle")}
			>
				<ApprovalModal videoId={approvalModal.id ?? 0} approved={false} close={() => setApprovalModal({ show: false })} />
			</Modal>
			<Modal show={deleteModal.show} onClose={() => setDeleteModal({ show: false })} size="sm" title="Delete">
				<DeleteModal videoId={deleteModal.id ?? 0} close={() => setDeleteModal({ show: false })} />
			</Modal>
			<Modal show={renameModal.show} onClose={() => setRenameModal({ show: false })} size="sm" title="Edit Name">
				{renameModal.show && (
					<RenameModal
						videoId={renameModal.id ?? 0}
						currentName={renameModal.name ?? ""}
						close={() => setRenameModal({ show: false })}
					/>
				)}
			</Modal>
			<Modal show={showBulkDeleteModal} onClose={() => setShowBulkDeleteModal(false)} size="sm" title="Delete">
				<div className="approveVideoModal-container">
					<div className="approveMessage">
						{selectedIds.size} uploads will be deleted and each file will have to be uploaded again. Are you sure you want to delete them?
					</div>
					<div className="buttons">
						<button className="submit" type="button" onClick={() => onConfirmBulkDelete()}>
							Delete
						</button>
						<button className="cancel" type="button" onClick={() => setShowBulkDeleteModal(false)}>
							Cancel
						</button>
					</div>
				</div>
			</Modal>
		</>
	);
}
